import { Button, CardBody, CardFooter, CardHeader } from "@heroui/react";
import React from "react";
import { useTranslation } from "react-i18next";

import { useAuth } from "@wc/domain/authn";
import InputList from "@wc/domain/components/win-card/input-list";
import WeekReviewModal from "@wc/domain/components/win-card/week-review-modal";
import { formatDate, formatDateInternal, lastMonday } from "@wc/domain/dates";
import {
  lockWeek,
  unlockWeek,
  useUserTeams,
  useWeekReview,
} from "@wc/domain/goals";
import { useOrg } from "@wc/domain/organizations";
import { ReactComponent as LockIcon } from "@wc/ui/assets/icons/lock.svg";
import { ReactComponent as UnlockIcon } from "@wc/ui/assets/icons/unlock.svg";
import SingleCardOnPage from "@wc/ui/components/single-card-on-page";

/**
 * The singlecardonpage component is a wrapper for a nextui card, just
 * use raw nextui components to fill it out.
 * @returns {React.ReactElement} A Editable WinCard
 */
export default function WinCardEditable() {
  const { t } = useTranslation();
  const authData = useAuth();
  const orgData = useOrg();
  const mondayDate = lastMonday();
  const monday = formatDate(mondayDate);
  const weekOf = formatDateInternal(mondayDate);

  const [listState, setListState] = React.useState({
    hasSavedGoals: false,
    isLocked: false,
  });
  const [isLocking, setIsLocking] = React.useState(false);

  const userId = authData.isLoggedIn() ? authData.getCurrentUser().id : null;
  const organizationId = orgData?.org?.id;
  const { primaryTeamId } = useUserTeams();

  const weekReview = useWeekReview();

  const handleLockToggle = async () => {
    if (!userId || !organizationId) return;

    setIsLocking(true);
    try {
      if (listState.isLocked) {
        await unlockWeek(userId, organizationId, weekOf, primaryTeamId);
        setListState((prev) => ({ ...prev, isLocked: false }));
      } else {
        await lockWeek(userId, organizationId, weekOf, primaryTeamId);
        setListState((prev) => ({ ...prev, isLocked: true }));
      }
    } catch (error) {
      console.error("Failed to toggle lock:", error);
    } finally {
      setIsLocking(false);
    }
  };

  const showLockButton = listState.hasSavedGoals;

  return (
    <>
      <WeekReviewModal
        isOpen={weekReview.isOpen}
        onClose={weekReview.onClose}
        goals={weekReview.previousWeekGoals}
        weekOf={weekReview.previousWeekKey}
        allComplete={weekReview.allComplete}
        onGoalToggle={weekReview.onGoalToggle}
        loading={weekReview.loading}
      />
      <SingleCardOnPage>
        <CardHeader className="mx-auto flex max-w-md justify-center">
          <h1 className="flex flex-wrap justify-center text-2xl font-bold sm:text-3xl">
            <span>{t("weekOf")}</span>
            <span className="text-primary ml-2">{monday}</span>
          </h1>
        </CardHeader>
        <CardBody className="flex justify-center">
          <div className="mx-auto mb-2 max-w-md px-4 text-center font-bold sm:px-6 lg:px-8">
            <p>{t("crush")}</p>
            <p>{t("add-i")}</p>
          </div>
          <InputList
            date={mondayDate}
            isLocked={listState.isLocked}
            onStateChange={setListState}
          />
        </CardBody>
        {showLockButton && (
          <CardFooter className="justify-end">
            <Button
              color={listState.isLocked ? "default" : "primary"}
              variant={listState.isLocked ? "flat" : "solid"}
              isLoading={isLocking}
              onPress={handleLockToggle}
              startContent={
                !isLocking &&
                (listState.isLocked ? (
                  <UnlockIcon className="size-5" />
                ) : (
                  <LockIcon className="size-5" />
                ))
              }
            >
              {listState.isLocked ? t("goals.unlock") : t("goals.lockIn")}
            </Button>
          </CardFooter>
        )}
      </SingleCardOnPage>
    </>
  );
}
